import { Check } from 'lucide-react';

import type { CoverageTier, TierProgress } from '@/core/types';
import { cn } from '@/lib/utils';

/**
 * Coverage-tier chips for catalog vocabulary apps.
 *
 * The sibling of LevelChips, for decks that aren't CEFR-graded. A tier says
 * how many exam questions a word appears in, which is the axis that actually
 * decides what to study first: rarity is not payoff, and a plain word in
 * fourteen questions earns more time than an exotic one in a single question.
 *
 * The summary line under the chips ("42 words unlock 180 of 310 questions") is
 * the motivating number — it turns an undifferentiated 284-word deck into a
 * visible shortest path through the exam.
 */

type Props = {
  tierProgress: TierProgress;
  /** Empty array = "all tiers" (no filter). */
  selected: CoverageTier[];
  onChange: (next: CoverageTier[]) => void;
};

const TIER_LABEL: Record<CoverageTier, string> = {
  1: '5+ questions',
  2: '2–4 questions',
  3: '1 question',
};

export function TierChips({ tierProgress, selected, onChange }: Props) {
  const { tiers, questions_covered_total } = tierProgress;
  const isAllTiers = selected.length === 0;

  const toggleTier = (tier: CoverageTier) => {
    if (selected.includes(tier)) {
      onChange(selected.filter((t) => t !== tier));
    } else {
      onChange([...selected, tier]);
    }
  };

  const shown = isAllTiers ? tiers : tiers.filter((t) => selected.includes(t.tier));
  const words = shown.reduce((sum, t) => sum + t.total, 0);

  // Per-tier question counts overlap — one question can contain words from
  // several tiers — so they cannot be added up. State a question figure only
  // when it is exactly known: the whole deck, or a single tier.
  const questions = isAllTiers
    ? questions_covered_total
    : shown.length === 1
      ? shown[0].questions_covered
      : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-stretch gap-2">
        <button
          type="button"
          onClick={() => onChange([])}
          aria-pressed={isAllTiers}
          className={cn(
            'rounded-full border px-4 py-2 text-sm font-medium transition',
            isAllTiers
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-foreground hover:bg-accent',
          )}
        >
          All words
        </button>

        {tiers.map((t) => {
          const isSelected = selected.includes(t.tier);
          const isDisabled = t.total === 0;
          const isComplete = t.total > 0 && t.memorized === t.total;
          const pct = t.total > 0 ? Math.round((t.memorized / t.total) * 100) : 0;

          return (
            <button
              key={t.tier}
              type="button"
              disabled={isDisabled}
              onClick={() => toggleTier(t.tier)}
              aria-pressed={isSelected}
              aria-label={`Words appearing in ${TIER_LABEL[t.tier]}`}
              className={cn(
                'flex min-w-24 flex-col items-center rounded-2xl border px-4 py-2 text-sm font-medium transition',
                'disabled:cursor-not-allowed disabled:opacity-50',
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : isComplete
                    ? 'border-success bg-success/10 text-success hover:bg-success/15'
                    : 'border-border bg-card text-foreground hover:bg-accent',
              )}
            >
              <span className="flex items-center gap-1 leading-none">
                {isComplete && <Check className="size-3.5" />}
                {TIER_LABEL[t.tier]}
              </span>
              <span className="mt-0.5 text-xs leading-none opacity-80">
                {isDisabled ? '—' : `${t.total} words · ${pct}%`}
              </span>
            </button>
          );
        })}
      </div>

      {words > 0 && (
        <p className="text-muted-foreground text-xs">
          {words} {words === 1 ? 'word' : 'words'}
          {questions !== null && (
            <>
              {' '}
              appearing in {questions} exam {questions === 1 ? 'question' : 'questions'}
            </>
          )}
        </p>
      )}
    </div>
  );
}
